import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  activateOrganization,
  createOrganization,
  fetchOrganizations,
  suspendOrganization,
} from "@/lib/platformApi";

export default function PlatformOrganizations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rfc, setRfc] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchOrganizations());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar organizaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await createOrganization({ name: name.trim(), rfc: rfc.trim().toUpperCase() });
      toast.success("Organización creada");
      setName("");
      setRfc("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear organización");
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Nueva organización</h2>
        <form onSubmit={onCreate} className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="org-name">Nombre comercial</Label>
            <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="org-rfc">RFC</Label>
            <Input id="org-rfc" value={rfc} onChange={(e) => setRfc(e.target.value)} required />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" className="bg-[#0F2D5C] hover:bg-[#0a2247]">
              Crear organización
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Organizaciones</h2>
        {loading ? (
          <p className="text-sm text-slate-500 mt-3">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">Sin organizaciones registradas.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {items.map((org) => (
              <li key={org.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-[#0F2D5C]">{org.name}</div>
                  <div className="text-xs text-slate-500">
                    {org.org_code ? (
                      <span className="font-mono">{org.org_code}</span>
                    ) : null}
                    {org.org_code ? " · " : ""}
                    RFC {org.rfc} · {org.status}
                    {org.primary_owner_user_id ? " · con administrador" : " · sin administrador"}
                  </div>
                </div>
                <div className="flex gap-2">
                  {org.status === "active" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await suspendOrganization(org.id);
                        toast.success("Organización suspendida");
                        reload();
                      }}
                    >
                      Suspender
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await activateOrganization(org.id);
                        toast.success("Organización reactivada");
                        reload();
                      }}
                    >
                      Reactivar
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
